import React, { useRef, useState } from 'react';
import ScrollAnimation from './ScrollAnimation';

/**
 * Endpoint que recibe el formulario.
 *
 * En producción es una ruta relativa a propósito: el vhost de www.ironfive.ar tiene un
 * `ProxyPass "/fundacion-api"` al balancer de esa API, así que para el browser es el mismo
 * origen y no hay CORS ni preflight de por medio.
 *
 * En desarrollo se apunta al backend local con REACT_APP_CONTACTO_URL en un .env.local:
 *   REACT_APP_CONTACTO_URL=http://localhost:8080/api/public/ironfive/contacto
 */
const ENDPOINT =
    process.env.REACT_APP_CONTACTO_URL ?? '/fundacion-api/api/public/ironfive/contacto';

type Estado = 'idle' | 'enviando' | 'ok' | 'error';

const Contact = () => {
    const [formData, setFormData] = useState({
        nombre: '',
        email: '',
        telefono: '',
        empresa: '',
        mensaje: '',
        website: '', // honeypot: oculto, tiene que quedar vacío
    });
    const [estado, setEstado] = useState<Estado>('idle');
    const [error, setError] = useState('');

    // Cuánto tardó en completarse el formulario. El backend descarta los envíos hechos en
    // menos de unos segundos: un humano no llega, un bot sí. useRef y no useState para que
    // fijar el momento inicial no dispare un re-render.
    const abiertoEn = useRef(Date.now());

    const handleChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (estado === 'enviando') return;

        setEstado('enviando');
        setError('');

        try {
            const res = await fetch(ENDPOINT, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ...formData, formMs: Date.now() - abiertoEn.current }),
            });

            // El backend contesta JSON en el ok y en el error (400/429). Si el proxy se cae,
            // lo que vuelve es un HTML de Apache y el .json() explota: lo atajamos abajo.
            const data = await res.json().catch(() => null);

            if (!res.ok) {
                setError(data?.error ?? 'No pudimos enviar el mensaje. Probá de nuevo en un rato.');
                setEstado('error');
                return;
            }

            setEstado('ok');
            setFormData({ nombre: '', email: '', telefono: '', empresa: '', mensaje: '', website: '' });
            abiertoEn.current = Date.now();
        } catch {
            setError(
                'No pudimos conectarnos. Escribinos directo a hola.ironfive@gmail.com y lo resolvemos por ahí.'
            );
            setEstado('error');
        }
    };

    const inputClass =
        'w-full px-4 py-2.5 border border-gray-300 rounded-md font-lato focus:outline-none focus:ring-2 focus:ring-cyan focus:border-transparent transition-all';

    return (
        <section id="contacto" className="py-16 bg-white">
            <div className="container mx-auto px-6">
                <ScrollAnimation>
                    <h2 className="text-3xl font-bold font-poppins text-grafito mb-4 text-center">
                        Contacto
                    </h2>
                </ScrollAnimation>
                <ScrollAnimation delay={0.1}>
                    <p className="text-center text-gray-600 font-lato max-w-2xl mx-auto mb-12">
                        Contanos qué necesitás. Si es algo que hacemos bien, te lo decimos; si no,
                        también.
                    </p>
                </ScrollAnimation>

                {/* Antes esto era `grid-cols-2` sin breakpoint: en un celular quedaban dos
                    columnas de 160px. Ahora apila en mobile. */}
                <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-12">
                    <ScrollAnimation delay={0.2}>
                        <div>
                            <h3 className="text-xl font-semibold font-montserrat text-grafito mb-4">
                                Información de contacto
                            </h3>
                            <div className="space-y-3">
                                <a
                                    href="mailto:hola.ironfive@gmail.com"
                                    className="flex items-center p-3 hover:bg-gray-50 rounded-lg transition-colors"
                                >
                                    <span className="text-cyan mr-3 text-xl" aria-hidden="true">📧</span>
                                    <span className="text-grafito font-lato">hola.ironfive@gmail.com</span>
                                </a>
                                <a
                                    href="https://www.ironfive.ar"
                                    className="flex items-center p-3 hover:bg-gray-50 rounded-lg transition-colors"
                                >
                                    <span className="text-cyan mr-3 text-xl" aria-hidden="true">🌐</span>
                                    <span className="text-grafito font-lato">www.ironfive.ar</span>
                                </a>
                                <div className="flex items-center p-3">
                                    <span className="text-cyan mr-3 text-xl" aria-hidden="true">📍</span>
                                    <span className="text-grafito font-lato">Buenos Aires, Argentina</span>
                                </div>
                            </div>

                            <div className="mt-8 p-5 bg-gray-50 rounded-lg border-l-4 border-lima">
                                <p className="text-sm text-gray-700 font-lato leading-relaxed">
                                    Respondemos rápido: el mensaje nos llega al celular apenas lo mandás.
                                    Si en 24 horas no tuviste noticias, escribinos al mail directo —
                                    algo se rompió y queremos saberlo.
                                </p>
                            </div>
                        </div>
                    </ScrollAnimation>

                    <ScrollAnimation delay={0.3}>
                        <div>
                            <h3 className="text-xl font-semibold font-montserrat text-grafito mb-4">
                                Envianos un mensaje
                            </h3>

                            {estado === 'ok' ? (
                                <div className="p-6 bg-lima bg-opacity-20 border border-lima rounded-lg">
                                    <p className="font-montserrat font-semibold text-grafito mb-1">
                                        ¡Listo, nos llegó!
                                    </p>
                                    <p className="text-sm text-gray-700 font-lato mb-4">
                                        Te respondemos a la brevedad al mail que dejaste.
                                    </p>
                                    <button
                                        type="button"
                                        onClick={() => setEstado('idle')}
                                        className="text-sm font-montserrat font-semibold text-cyan hover:underline"
                                    >
                                        Mandar otro mensaje
                                    </button>
                                </div>
                            ) : (
                                <form onSubmit={handleSubmit} className="space-y-4" noValidate>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        <div>
                                            <label htmlFor="nombre" className="block text-grafito font-lato mb-1 text-sm">
                                                Nombre <span className="text-cyan">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                id="nombre"
                                                name="nombre"
                                                value={formData.nombre}
                                                onChange={handleChange}
                                                required
                                                autoComplete="name"
                                                className={inputClass}
                                            />
                                        </div>
                                        <div>
                                            <label htmlFor="email" className="block text-grafito font-lato mb-1 text-sm">
                                                Email <span className="text-cyan">*</span>
                                            </label>
                                            <input
                                                type="email"
                                                id="email"
                                                name="email"
                                                value={formData.email}
                                                onChange={handleChange}
                                                required
                                                autoComplete="email"
                                                className={inputClass}
                                            />
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        <div>
                                            <label htmlFor="telefono" className="block text-grafito font-lato mb-1 text-sm">
                                                Teléfono
                                            </label>
                                            <input
                                                type="tel"
                                                id="telefono"
                                                name="telefono"
                                                value={formData.telefono}
                                                onChange={handleChange}
                                                autoComplete="tel"
                                                className={inputClass}
                                            />
                                        </div>
                                        <div>
                                            <label htmlFor="empresa" className="block text-grafito font-lato mb-1 text-sm">
                                                Empresa
                                            </label>
                                            <input
                                                type="text"
                                                id="empresa"
                                                name="empresa"
                                                value={formData.empresa}
                                                onChange={handleChange}
                                                autoComplete="organization"
                                                className={inputClass}
                                            />
                                        </div>
                                    </div>

                                    <div>
                                        <label htmlFor="mensaje" className="block text-grafito font-lato mb-1 text-sm">
                                            Mensaje <span className="text-cyan">*</span>
                                        </label>
                                        <textarea
                                            id="mensaje"
                                            name="mensaje"
                                            value={formData.mensaje}
                                            onChange={handleChange}
                                            required
                                            rows={5}
                                            placeholder="Contanos qué querés construir, o qué problema tenés hoy."
                                            className={inputClass}
                                        />
                                    </div>

                                    {/* Honeypot. `hidden` a secas y no display:none por CSS, para que
                                        un bot que ignora el CSS igual lo vea y lo complete.
                                        tabIndex/-1 y aria-hidden lo sacan del camino de un lector de
                                        pantalla y del tabulador. */}
                                    <div className="hidden" aria-hidden="true">
                                        <label htmlFor="website">No completes este campo</label>
                                        <input
                                            type="text"
                                            id="website"
                                            name="website"
                                            value={formData.website}
                                            onChange={handleChange}
                                            tabIndex={-1}
                                            autoComplete="off"
                                        />
                                    </div>

                                    {estado === 'error' && (
                                        <p role="alert" className="text-sm text-red-600 font-lato">
                                            {error}
                                        </p>
                                    )}

                                    <button
                                        type="submit"
                                        disabled={estado === 'enviando'}
                                        className="bg-cyan text-white px-6 py-3 rounded-md hover:bg-opacity-90 transition-all font-montserrat font-semibold w-full sm:w-auto disabled:opacity-60 disabled:cursor-not-allowed"
                                    >
                                        {estado === 'enviando' ? 'Enviando…' : 'Enviar mensaje'}
                                    </button>
                                </form>
                            )}
                        </div>
                    </ScrollAnimation>
                </div>
            </div>
        </section>
    );
};

export default Contact;
